

































import { maxBy, minBy } from 'lodash'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'

import CustomEditor from '../../logic/Editor/CustomEditor'
import miniNode from './minimapNode.vue'
import { MiniNode, Transform } from './types'

@Component({
  components: {
    miniNode
  }
})
export default class Minimap extends Vue {
  @Prop() editor!: CustomEditor

  @Watch('editor.nodes', { immediate: true })
  nodesWatcher(): void {
    this.createMiniNodesConfig()
  }

  visible = {
    width: 50,
    height: 50,
    x: 0,
    y: 0,
  }

  scale = 0.1

  rectangle = {
    position: {
      x: 0,
      y: 0,
    },
    height: 0,
    width: 0,
    largeSide: 0,
    lesserSide: 0
  }

  nodesParams: Record<number, MiniNode> = {}

  transferTrigger = false

  get nodeValues() {
    return Object.values(this.nodesParams)
  }

  createMiniNodesConfig(): void {
    this.nodesParams = {}

    this.editor.nodes.forEach((n) => {
      const nv = this.editor.view.nodes.get(n)

      if (nv) {
        const x = n.position[0]
        const y = n.position[1]

        const height = nv.el.clientHeight
        const width = nv.el.clientWidth

        this.nodesParams[n.id] = {
          nodeID: n.id,
          left: x,
          top: y,
          bottom: y + height,
          right: x + width,
          height: height,
          width: width,
          x: 0,
          y: 0,
          color: n.color
        }
      }
    })

    this.getMapField()
    this.getNodesPosition()

    const transform = this.editor.view.area.transform
    this.getLocationVisibleField({
      x: transform.x * (-1),
      y: transform.y * (-1),
      k: transform.k
    })
  }

  positionTransfer(): void {
    if(this.transferTrigger) {
      const e = event as MouseEvent

      let x = 0
      let y = 0

      const scaledXRect = this.rectangle.position.x * this.scale
      const scaledYRect = this.rectangle.position.y * this.scale

      const largeCentering = (200 - this.rectangle.largeSide * this.scale) / 2
      const lesserCentering = (200 - this.rectangle.lesserSide * this.scale) / 2

      if(this.rectangle.width > this.rectangle.height) {
        x = (scaledXRect + e.offsetX - largeCentering) / this.scale * this.editor.view.area.transform.k
        y = (scaledYRect + e.offsetY - lesserCentering) / this.scale * this.editor.view.area.transform.k
      } else {
        x = (scaledXRect + e.offsetX - lesserCentering) / this.scale * this.editor.view.area.transform.k
        y = (scaledYRect + e.offsetY - largeCentering) / this.scale * this.editor.view.area.transform.k
      }

      x -= this.editor.view.container.clientWidth / 2
      y -= this.editor.view.container.clientHeight / 2

      this.editor.view.area.translate(x * (-1), y * (-1))
    }
  }

  clickMove(): void {
    this.transferTrigger = true
    this.positionTransfer()
    this.transferTrigger = false
  }

  getLocationVisibleField({ x, y, k } : Transform): void {
    const coords = this.convertingMapLocation(x / k, y / k)
    this.visible = {
      x: coords.cx,
      y: coords.cy,
      width: this.editor.view.container.clientWidth / k * this.scale,
      height: (this.editor.view.container.clientHeight / k) * this.scale
    }
  }

  convertingMapLocation(x: number, y: number): {cx: number, cy: number}{
    const largeSide = this.rectangle.largeSide
    const lesserSide = this.rectangle.lesserSide

    const rectX = this.rectangle.position.x
    const rectY = this.rectangle.position.y

    let cx = 0
    let cy = 0

    if(this.rectangle.width > this.rectangle.height) {
      cx = (((x - rectX) * this.scale) + (200 - largeSide * this.scale) / 2)
      cy = (((y - rectY) * this.scale) + (200 - lesserSide * this.scale) / 2)
    } else {
      cx = (((x - rectX) * this.scale) + (200 - lesserSide * this.scale) / 2)
      cy = (((y - rectY) * this.scale) + (200 - largeSide * this.scale) / 2)
    }

    return { cx, cy }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  getMapField() {
    const mapField = {
      top: minBy(this.nodeValues, item => item.top)?.top ?? 0,
      left: minBy(this.nodeValues, item => item.left)?.left ?? 0,
      bottom: maxBy(this.nodeValues, item => item.bottom)?.bottom ?? 0,
      right: maxBy(this.nodeValues, item => item.right)?.right ?? 0,
    }

    const { top, left, bottom, right } = mapField

    const calcSide = (a:number, b:number) => {
      if (a < 0 && b > 0) {
        return Math.abs(a) + Math.abs(b)
      } else if (a < 0 && b < 0) {
        const absNegativeMax = Math.max(Math.abs(a), Math.abs(b))
        const absNegativeMin = Math.min(Math.abs(a), Math.abs(b))

        return absNegativeMax - absNegativeMin
      } else {
        return Math.max(a, b) - Math.min(a, b)
      }
    }

    const height = calcSide(top, bottom);
    const width = calcSide(left, right)

    this.rectangle = {
      position: {
        x: left,
        y: top
      },
      height: height,
      width: width,
      largeSide: Math.max(height, width),
      lesserSide: Math.min(height, width)
    }

    if((200 / this.rectangle.largeSide) < 0.099){
      this.scale = 200 / this.rectangle.largeSide
    } else {
      this.scale = 0.1
    }
  }

  getNodesPosition(): void {
    this.nodeValues.forEach(node => {
      const coords = this.convertingMapLocation(node.left, node.top)
      node.x = coords.cx
      node.y = coords.cy
    })
  }

  mounted(): void {
    this.editor.on('nodetranslate' , ({ node, x, y }) => {
      this.createMiniNodesConfig()
    })
      .on('translate', ({ transform, x, y }) => {
        this.$nextTick(() => {
          this.getLocationVisibleField({
            x: transform.x * -1,
            y: transform.y * -1,
            k: transform.k
          })
        })
      })
      // eslint-disable-next-line sonarjs/no-identical-functions
      .on('zoom', ({ transform, source, zoom }) => {
        // eslint-disable-next-line sonarjs/no-identical-functions
        this.$nextTick(() => {
          this.getLocationVisibleField({
            x: transform.x * -1,
            y: transform.y * -1,
            k: transform.k
          })
        })
      })
  }
}
